import { useState, type ReactNode } from "react";
import {
  Building2, Users, TrendingUp, Lightbulb, Handshake, HelpCircle, Briefcase, UserCheck, Crown, ChevronDown, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SelectableCard } from "../SelectableCard";
import type { RoleOption } from "../roleConfig";
import {
  REVENUE_STAGE_OPTIONS, FIRST_TASK_OPTIONS,
  type RoleKey, type UseCase, type BuildScope, type RevenueStage, type FirstTask,
  type DetectedPerson, type SelfMatchDecision,
} from "../types";
import {
  fundingForWizardStage, raiseMidpoint, ticketMidpoint, money, range,
} from "@/components/public/data/fundingStages";

// ── Shared question-page frame ────────────────────────────────────────────────

function QuestionShell({ subtitle, children }: { subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="max-w-2xl mx-auto px-6 py-10 flex flex-col gap-5">
      {subtitle && (
        <p className="text-lg text-muted-foreground/70 leading-relaxed">{subtitle}</p>
      )}
      {children}
    </div>
  );
}

// Consistent question header across every step: a short, unambiguous question with
// a single help tooltip carrying the fuller explanation. One design everywhere.
function QuestionHeader({ question, tooltip }: { question: string; tooltip: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      {question}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full hover:bg-muted/40 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50"
            aria-label="Why we ask"
          >
            <HelpCircle className="h-4 w-4 text-muted-foreground/70" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-left">
          <p className="font-medium mb-1">Why we ask</p>
          <p className="text-sm leading-relaxed opacity-90">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

// ── Step 2 — Role ─────────────────────────────────────────────────────────────

const ROLE_TOOLTIPS: Record<string, ReactNode> = {
  governance_role: (
    <>
      <p className="font-medium mb-0.5">Governance relationship</p>
      <p className="text-sm leading-relaxed opacity-90">You founded, own, advise, or otherwise govern this entity. Raisy records this as a governance_role relationship.</p>
    </>
  ),
  employee: (
    <>
      <p className="font-medium mb-0.5">Employment relationship</p>
      <p className="text-sm leading-relaxed opacity-90">You work for this entity or provide services under an employment-style relationship.</p>
    </>
  ),
  investor: (
    <>
      <p className="font-medium mb-0.5">Investor relationship</p>
      <p className="text-sm leading-relaxed opacity-90">You have invested capital in this entity. Raisy records this as a dedicated investor relationship.</p>
    </>
  ),
  partner: (
    <>
      <p className="font-medium mb-0.5">Partnership relationship</p>
      <p className="text-sm leading-relaxed opacity-90">You collaborate with this entity through a mutual partnership or strategic alliance.</p>
    </>
  ),
  supplier: (
    <>
      <p className="font-medium mb-0.5">Supplier relationship</p>
      <p className="text-sm leading-relaxed opacity-90">You supply goods or services to this entity.</p>
    </>
  ),
  client: (
    <>
      <p className="font-medium mb-0.5">Client / customer relationship</p>
      <p className="text-sm leading-relaxed opacity-90">You buy from or use the offerings of this entity.</p>
    </>
  ),
};

function roleTooltip(option: RoleOption): ReactNode {
  if (option.key === "other") {
    return (
      <>
        <p className="font-medium mb-0.5">Custom relationship</p>
        <p className="text-sm leading-relaxed opacity-90">Describe a relationship that does not fit the preset options.</p>
      </>
    );
  }
  return ROLE_TOOLTIPS[option.relationshipType] ?? ROLE_TOOLTIPS.governance_role;
}

const ROLE_ICONS: Record<string, ReactNode> = {
  founder_ceo: <Building2 className="h-4 w-4" />,
  co_founder:  <UserCheck  className="h-4 w-4" />,
  employee:    <Briefcase  className="h-4 w-4" />,
  investor:    <TrendingUp className="h-4 w-4" />,
  advisor:     <Lightbulb  className="h-4 w-4" />,
  partner:     <Handshake  className="h-4 w-4" />,
  freelancer:  <Users      className="h-4 w-4" />,
  other:       <HelpCircle className="h-4 w-4" />,
};

export function namesOverlap(a: string, b: string): boolean {
  if (!a || !b) return false;
  const tokens = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/).filter((t) => t.length > 1);
  const setA = new Set(tokens(a));
  return tokens(b).some((t) => setA.has(t));
}

export function StepRole({
  options,
  selectedRoles,
  onSelectRole,
  roleOther,
  onSetRoleOther,
}: {
  options: RoleOption[];
  selectedRoles: RoleKey[];
  onSelectRole: (role: RoleKey) => void;
  roleOther: string;
  onSetRoleOther: (value: string) => void;
}) {
  const otherSelected = selectedRoles.includes("other");
  return (
    <QuestionShell
      subtitle={
        <QuestionHeader
          question="How do you relate to this entity?"
          tooltip="Raisy records this as the relationship between you and this entity. It shapes which processes and bots make sense for you here."
        />
      }
    >
      <div className="grid grid-cols-4 gap-1.5">
        {options.map((option) => (
          <SelectableCard
            key={option.key}
            layout="tile"
            selected={selectedRoles.includes(option.key)}
            onClick={() => onSelectRole(option.key)}
            icon={option.iconKey ? ROLE_ICONS[option.iconKey] : ROLE_ICONS[option.key] ?? <HelpCircle className="h-4 w-4" />}
            label={option.label}
            tooltip={roleTooltip(option)}
          />
        ))}
      </div>
      {otherSelected && (
        <Input
          autoFocus
          value={roleOther}
          onChange={(e) => onSetRoleOther(e.target.value)}
          placeholder="Please specify your role…"
          className="text-lg"
        />
      )}
    </QuestionShell>
  );
}

// ── Step 7 — Identity match ("Is this you?") ──────────────────────────────────

export function StepIsThisYou({
  matchedPerson, decision, onDecide, claimState, onRetryClaim,
}: {
  matchedPerson: DetectedPerson;
  decision: SelfMatchDecision | null;
  onDecide: (d: SelfMatchDecision) => void;
  // T0119 / project077 D-001 — the claim executes at click time; the card shows
  // its real outcome (loading / linked / failed + retry) instead of a promise.
  claimState: { status: "idle" | "claiming" | "claimed" | "failed"; error?: string };
  onRetryClaim: () => void;
}) {
  return (
    <QuestionShell subtitle="We found someone matching your name in this company's team list. Is that you?">
      <div className={cn(
        "rounded-xl border p-6 flex flex-col gap-5 transition-all",
        decision === null
          ? "border-amber-400/40 bg-amber-500/[0.06]"
          : decision.isMe
          ? claimState.status === "failed"
            ? "border-red-400/40 bg-red-500/[0.06]"
            : "border-emerald-400/40 bg-emerald-500/[0.06]"
          : "border-border/40 bg-muted/10",
      )}>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
            <Crown className="h-5 w-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xl font-semibold text-foreground">{matchedPerson.entity_name}</p>
            <p className="text-md text-muted-foreground mt-0.5">Found in this company's team list</p>
          </div>
        </div>

        {decision === null ? (
          <div className="flex gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onDecide({ isMe: true, entityId: matchedPerson.entity_id })}
                  className="flex-1 h-10 rounded-lg bg-amber-500 hover:bg-amber-400 text-white text-lg font-medium transition-colors"
                >
                  Yes, that's me
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px] text-left">
                <p className="font-medium mb-0.5">Claim this profile</p>
                <p className="text-sm leading-relaxed opacity-90">Link this extracted person entity to your Raisy account so it appears as you.</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onDecide({ isMe: false, entityId: null })}
                  className="flex-1 h-10 rounded-lg border border-border/60 text-lg font-medium text-muted-foreground hover:text-foreground hover:border-border transition-colors"
                >
                  No, not me
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px] text-left">
                <p className="font-medium mb-0.5">Keep separate</p>
                <p className="text-sm leading-relaxed opacity-90">Leave this as a separate team member. You can claim it later from the entity profile.</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : decision.isMe ? (
          <div className="flex flex-col items-center gap-2">
            {claimState.status === "claiming" && (
              <p className="text-lg text-center font-medium py-1 text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Linking this profile to your account…
              </p>
            )}
            {claimState.status === "claimed" && (
              <p className="text-lg text-center font-medium py-1 text-emerald-500">
                Linked to your account
              </p>
            )}
            {claimState.status === "failed" && (
              <>
                <p className="text-md text-center text-red-400">
                  {claimState.error ?? "Couldn't link this profile to your account."}
                </p>
                <button
                  onClick={onRetryClaim}
                  className="h-9 px-4 rounded-lg border border-border/60 text-md font-medium text-muted-foreground hover:text-foreground hover:border-border transition-colors"
                >
                  Try again
                </button>
              </>
            )}
            {claimState.status === "idle" && (
              <p className="text-lg text-center font-medium py-1 text-emerald-500">
                This profile will be linked to your account
              </p>
            )}
          </div>
        ) : (
          <p className="text-lg text-center font-medium py-1 text-muted-foreground">
            This will stay as a separate team member
          </p>
        )}
      </div>

      <p className="text-md text-muted-foreground/70 text-center">
        {decision === null
          ? "Your answer determines how this profile is connected to your Raisy account."
          : "You can change this later from the entity profile page."}
      </p>
    </QuestionShell>
  );
}

// ── Step 3 — Goals (multi-select) ─────────────────────────────────────────────

const USE_CASE_TOOLTIPS: Record<string, ReactNode> = {
  operations: (
    <>
      <p className="font-medium mb-0.5">Operations</p>
      <p className="text-sm leading-relaxed opacity-90">How the work gets done day-to-day — execution, delivery, and internal workflows.</p>
    </>
  ),
  sales: (
    <>
      <p className="font-medium mb-0.5">Sales</p>
      <p className="text-sm leading-relaxed opacity-90">Finding prospects, managing deals, and converting them into customers.</p>
    </>
  ),
  marketing: (
    <>
      <p className="font-medium mb-0.5">Marketing</p>
      <p className="text-sm leading-relaxed opacity-90">Awareness, messaging, content, campaigns, and pipeline generation.</p>
    </>
  ),
  product: (
    <>
      <p className="font-medium mb-0.5">Product</p>
      <p className="text-sm leading-relaxed opacity-90">Building and improving what you offer — roadmap, features, and releases.</p>
    </>
  ),
  finance: (
    <>
      <p className="font-medium mb-0.5">Finance</p>
      <p className="text-sm leading-relaxed opacity-90">Budgeting, reporting, fundraising, accounting, and financial operations.</p>
    </>
  ),
  "customer success": (
    <>
      <p className="font-medium mb-0.5">Customer Success</p>
      <p className="text-sm leading-relaxed opacity-90">Keeping customers happy, supported, and retained after they buy.</p>
    </>
  ),
  hr: (
    <>
      <p className="font-medium mb-0.5">HR / People</p>
      <p className="text-sm leading-relaxed opacity-90">Hiring, onboarding, culture, and people operations.</p>
    </>
  ),
  legal: (
    <>
      <p className="font-medium mb-0.5">Legal / Compliance</p>
      <p className="text-sm leading-relaxed opacity-90">Contracts, compliance, governance, and risk management.</p>
    </>
  ),
};

function useCaseTooltip(uc: UseCase): ReactNode {
  const normalized = uc.label.toLowerCase();
  return (
    USE_CASE_TOOLTIPS[normalized] ?? (
      <>
        <p className="font-medium mb-0.5">{uc.label}</p>
        <p className="text-sm leading-relaxed opacity-90">
          {uc.description || "Raisy will prioritise processes and bots in this business domain."}
        </p>
      </>
    )
  );
}

export function StepGoals({
  useCaseOptions, selectedUseCases, onToggleUseCase,
}: {
  useCaseOptions: UseCase[];
  selectedUseCases: string[];
  onToggleUseCase: (key: string) => void;
}) {
  return (
    <QuestionShell
      subtitle={
        <QuestionHeader
          question="What should Raisy help with?"
          tooltip="These are the business domains Raisy will build processes and bots for. Pick every domain that matters to this entity — you can change this later."
        />
      }
    >
      <div className="flex flex-col gap-1.5">
        {useCaseOptions.map((uc) => (
          <SelectableCard
            key={uc.key}
            selected={selectedUseCases.includes(uc.key)}
            onClick={() => onToggleUseCase(uc.key)}
            label={uc.label}
            description={uc.description}
            tooltip={useCaseTooltip(uc)}
          />
        ))}
      </div>
    </QuestionShell>
  );
}

// ── Step 4 — Build Scope (single-select) ──────────────────────────────────────

const BUILD_SCOPE_OPTIONS: { key: Exclude<BuildScope, "">; label: string; description: string; tooltip: ReactNode }[] = [
  {
    key: "light",
    label: "Light",
    description: "Core execution only (~2–3 processes)",
    tooltip: (
      <>
        <p className="font-medium mb-0.5">Light build</p>
        <p className="text-sm leading-relaxed opacity-90">Raisy builds only the most essential execution processes. Fastest setup, smallest coverage.</p>
      </>
    ),
  },
  {
    key: "standard",
    label: "Standard",
    description: "Full execution (~8–12 processes, L0–L1)",
    tooltip: (
      <>
        <p className="font-medium mb-0.5">Standard build</p>
        <p className="text-sm leading-relaxed opacity-90">Raisy builds the full operating model across the entity and its direct products or services.</p>
      </>
    ),
  },
  {
    key: "deep",
    label: "Deep",
    description: "Deep execution + sub-processes (~15–20+, L0–L2)",
    tooltip: (
      <>
        <p className="font-medium mb-0.5">Deep build</p>
        <p className="text-sm leading-relaxed opacity-90">Raisy builds deep operations including sub-processes and nested functions. Highest coverage.</p>
      </>
    ),
  },
];

export function StepBuildScope({
  buildScope, onSetBuildScope,
}: {
  buildScope: BuildScope;
  onSetBuildScope: (val: BuildScope) => void;
}) {
  return (
    <QuestionShell
      subtitle={
        <QuestionHeader
          question="How deep should Raisy build?"
          tooltip="Depth controls how many processes Raisy sets up — from a small core set to deep operations with sub-processes. You can always build more later."
        />
      }
    >
      <div className="flex flex-col gap-2">
        {BUILD_SCOPE_OPTIONS.map(({ key, label, description, tooltip }) => (
          <SelectableCard
            key={key}
            selected={buildScope === key}
            onClick={() => onSetBuildScope(key)}
            label={label}
            description={description}
            tooltip={tooltip}
            trailing={
              key === "standard" ? (
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-label-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  Recommended
                </span>
              ) : undefined
            }
          />
        ))}
      </div>
    </QuestionShell>
  );
}

// ── Step 5 — Revenue stage (single-select, NEW) ───────────────────────────────

const REVENUE_STAGE_TOOLTIPS: Record<Exclude<RevenueStage, "" | null | undefined>, ReactNode> = {
  idea: (
    <>
      <p className="font-medium mb-0.5">Idea stage</p>
      <p className="text-sm leading-relaxed opacity-90">You have a concept but nothing is built yet. Raisy will focus on validation and planning workflows.</p>
    </>
  ),
  mvp: (
    <>
      <p className="font-medium mb-0.5">MVP stage</p>
      <p className="text-sm leading-relaxed opacity-90">You are building a first prototype or minimum viable product.</p>
    </>
  ),
  pre_revenue: (
    <>
      <p className="font-medium mb-0.5">Pre-revenue</p>
      <p className="text-sm leading-relaxed opacity-90">Product or service exists, but you are not yet charging customers.</p>
    </>
  ),
  early_revenue: (
    <>
      <p className="font-medium mb-0.5">Early revenue</p>
      <p className="text-sm leading-relaxed opacity-90">You have first paying customers and are finding product-market fit.</p>
    </>
  ),
  growth: (
    <>
      <p className="font-medium mb-0.5">Growth stage</p>
      <p className="text-sm leading-relaxed opacity-90">You are scaling revenue, team, and operations.</p>
    </>
  ),
  scale: (
    <>
      <p className="font-medium mb-0.5">Scale stage</p>
      <p className="text-sm leading-relaxed opacity-90">Established and optimising — focus shifts to efficiency and automation.</p>
    </>
  ),
};

export function StepRevenueStage({
  revenueStage, onSetRevenueStage,
}: {
  revenueStage: RevenueStage;
  onSetRevenueStage: (val: RevenueStage) => void;
}) {
  const funding = revenueStage ? fundingForWizardStage(revenueStage) : null;
  // The suggested raise-profile panel is COLLAPSED by default — the stage cards are
  // the question; the benchmark is a guide the user can open when they want it.
  const [showRaiseGuide, setShowRaiseGuide] = useState(false);
  return (
    <QuestionShell
      subtitle={
        <QuestionHeader
          question="What stage is this entity at?"
          tooltip="The one thing Raisy can't infer from your website — and the anchor of your whole raise profile. It drives the round, typical raise amount, ticket size and investor types we prefill. Benchmarks, not limits: you stay in full control and can change any of them on your raise page."
        />
      }
    >
      <div className="flex flex-col gap-2">
        {REVENUE_STAGE_OPTIONS.map(({ key, label, description }) => {
          const f = fundingForWizardStage(key);
          return (
            <SelectableCard
              key={key}
              selected={revenueStage === key}
              onClick={() => onSetRevenueStage(key)}
              label={label}
              description={description}
              trailing={f ? f.round : undefined}
              tooltip={REVENUE_STAGE_TOOLTIPS[key]}
            />
          );
        })}
      </div>

      {funding && (
        <div className="mt-1 rounded-xl border border-blue-500/30 bg-blue-500/[0.06] overflow-hidden">
          <button
            type="button"
            onClick={() => setShowRaiseGuide((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
            aria-expanded={showRaiseGuide}
          >
            <span className="flex items-center gap-2 min-w-0">
              <ChevronDown className={`h-4 w-4 text-blue-400 shrink-0 transition-transform ${showRaiseGuide ? "rotate-180" : ""}`} />
              <span className="text-lg font-semibold text-foreground truncate">
                {funding.round} — your suggested raise profile
              </span>
            </span>
            <span className="rounded-full bg-blue-500/15 text-blue-400 px-2.5 py-0.5 text-sm font-medium shrink-0">
              raise {range(funding.raiseMin, funding.raiseMax, funding.raiseOpen)}
            </span>
          </button>
          {showRaiseGuide && (
            <div className="px-4 pb-4 flex flex-col gap-3 border-t border-blue-500/10 pt-3">
              {funding.whatThisMeans && (
                <p className="text-md text-muted-foreground/80 leading-relaxed">{funding.whatThisMeans}</p>
              )}

              <div className="flex flex-wrap gap-2">
                <div className="rounded-lg border border-border/50 px-3 py-1.5">
                  <p className="text-label uppercase tracking-wide text-subtle-foreground">Suggested raise</p>
                  <p className="text-lg font-semibold text-foreground">{money(raiseMidpoint(funding))}</p>
                </div>
                <div className="rounded-lg border border-border/50 px-3 py-1.5">
                  <p className="text-label uppercase tracking-wide text-subtle-foreground">Suggested ticket</p>
                  <p className="text-lg font-semibold text-foreground">{money(ticketMidpoint(funding))}</p>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <p className="text-sm uppercase tracking-wide text-subtle-foreground font-medium">Investors to target</p>
                <div className="flex flex-wrap gap-1.5">
                  {funding.investors.map((inv) => (
                    <span key={inv.type} className="rounded-md border border-border/50 px-2 py-0.5 text-sm text-muted-foreground">
                      {inv.type} · {range(inv.ticketMin, inv.ticketMax, inv.open)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <p className="text-sm uppercase tracking-wide text-subtle-foreground font-medium">What you must show</p>
                <p className="text-md text-muted-foreground/80 leading-relaxed">{funding.maturityGate}</p>
              </div>

              <p className="text-sm leading-relaxed text-muted-foreground/70">
                These are benchmarks, not limits. Raising $2M at pre-seed? Type it. Name your round
                anything you like — you'll adjust all of this on your raise page.
              </p>
              <a
                href="/blog/startup-funding-stages-explained"
                className="text-md text-blue-400 hover:text-blue-300 underline underline-offset-2"
              >
                See the full funding-stages guide →
              </a>
            </div>
          )}
        </div>
      )}
    </QuestionShell>
  );
}

// ── Step 6 — Priorities (multi-select + "other" free text, NEW) ───────────────

const FIRST_TASK_TOOLTIPS: Record<FirstTask, ReactNode> = {
  raise_money: (
    <>
      <p className="font-medium mb-0.5">Raise money</p>
      <p className="text-sm leading-relaxed opacity-90">Prepare fundraising materials, investor outreach, and cap-table workflows.</p>
    </>
  ),
  lead_generation: (
    <>
      <p className="font-medium mb-0.5">Lead generation</p>
      <p className="text-sm leading-relaxed opacity-90">Find and attract new prospects into your pipeline.</p>
    </>
  ),
  admin: (
    <>
      <p className="font-medium mb-0.5">Admin & data entry</p>
      <p className="text-sm leading-relaxed opacity-90">Automate repetitive paperwork, data capture, and record keeping.</p>
    </>
  ),
  follow_ups: (
    <>
      <p className="font-medium mb-0.5">Follow-ups & outreach</p>
      <p className="text-sm leading-relaxed opacity-90">Chase replies, send check-ins, and keep conversations moving.</p>
    </>
  ),
  reporting: (
    <>
      <p className="font-medium mb-0.5">Reporting</p>
      <p className="text-sm leading-relaxed opacity-90">Pull numbers and updates into dashboards and reports.</p>
    </>
  ),
  scheduling: (
    <>
      <p className="font-medium mb-0.5">Scheduling</p>
      <p className="text-sm leading-relaxed opacity-90">Book meetings, coordinate calendars, and manage time.</p>
    </>
  ),
  research: (
    <>
      <p className="font-medium mb-0.5">Research</p>
      <p className="text-sm leading-relaxed opacity-90">Find, summarise, and monitor information on markets, people, or competitors.</p>
    </>
  ),
  other: (
    <>
      <p className="font-medium mb-0.5">Something else</p>
      <p className="text-sm leading-relaxed opacity-90">Describe a priority not listed and Raisy will capture it.</p>
    </>
  ),
};

export function StepFirstTask({
  firstTasks, onToggleFirstTask, firstTaskOther, onSetFirstTaskOther,
}: {
  firstTasks: FirstTask[];
  onToggleFirstTask: (val: FirstTask) => void;
  firstTaskOther: string;
  onSetFirstTaskOther: (val: string) => void;
}) {
  const otherSelected = firstTasks.includes("other");
  return (
    <QuestionShell
      subtitle={
        <QuestionHeader
          question="What should Raisy focus on first?"
          tooltip="These are the first priorities Raisy will work on for you. Pick all that apply — it will start with these."
        />
      }
    >
      <div className="flex flex-col gap-2">
        {FIRST_TASK_OPTIONS.map(({ key, label, description }) => (
          <SelectableCard
            key={key}
            selected={firstTasks.includes(key)}
            onClick={() => onToggleFirstTask(key)}
            label={label}
            description={description}
            tooltip={FIRST_TASK_TOOLTIPS[key]}
          />
        ))}
      </div>
      {otherSelected && (
        <Textarea
          autoFocus
          rows={3}
          value={firstTaskOther}
          onChange={(e) => onSetFirstTaskOther(e.target.value)}
          placeholder="Tell us what else Raisy should prioritise…"
          className="resize-none text-lg"
        />
      )}
    </QuestionShell>
  );
}
